import { useEffect, useRef, useState } from "react";
import styles from "./ChoiceModal.module.scss";
import WithModal, { CommonModalPropsI } from "@components/HOC/withModals";
import { MODALS_ENUM } from "@constants/Modals";
import Title from "@components/global/new/Title/Title";
import Button from "@components/global/new/Button/Button";
import { ReactComponent as CloseIcon } from "@assets/icons/close.svg";
import { Dispatch } from "redux";
import { show } from "redux-modal";
import cn from "classnames"
export interface ChoiceModalExposedPropsI {
  titleID: string;
  subtitleID?: string;
  optionIDs: string[];
  onConfirm(option: number): void;
}

interface ChoiceModalPropsI
  extends CommonModalPropsI,
    ChoiceModalExposedPropsI {}

function ChoiceModal({
  titleID,
  handleClose,
  onConfirm,
  formatMessage,
  ...props
}: ChoiceModalPropsI) {

  const [defaultButtonPos, setDefaultButtonPos] = useState(0);

  const onConfirmHandler = (option: number) => {
    onConfirm(option);
    handleClose();
  };

  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current && ref.current.parentElement) {
      ref.current.parentElement.style.width = "380px";
    }
  }, [ref]);

  useEffect(() => {
    const cb = (ev: KeyboardEvent) => {
      switch (ev.key) {
      case "Enter":
        onConfirmHandler(defaultButtonPos);
        break;
      case "ArrowLeft":
        setDefaultButtonPos(p => (p - 1) % props.optionIDs.length);
        break;
      case "ArrowRight":
        setDefaultButtonPos(p => (p + 1) % props.optionIDs.length);
        break;
      default: return;
      }
      ev.preventDefault();
      ev.stopPropagation();
    }
    document.addEventListener("keydown", cb);
    return () => {
      document.removeEventListener("keydown", cb);
    };
  }, [defaultButtonPos]);

  return (
    <div className={styles.wrapper} ref={ref}>
      <div className={styles.title_wrapper}>
        <Title className={styles.title}>
          {formatMessage({ id: titleID ?? "modal.choice.title" })}
        </Title>
        <CloseIcon className={styles.close} onClick={handleClose} />
      </div>
      {props.subtitleID && (
        <Title className={styles.subtitle}>{formatMessage({ id: props.subtitleID })}</Title>
      )}

      <div className={styles.btns}>
        {props.optionIDs?.map((id, pos) => 
          <Button 
            key={id} 
            className={cn(styles.btn, {[styles.notdefault]: pos !== defaultButtonPos})} 
            onClick={() => onConfirmHandler(pos)}
          >
            {formatMessage({ id })}
          </Button>
        )}
      </div>
    </div>
  );
}

export default WithModal(MODALS_ENUM.CHOICE_MODAL, ChoiceModal, {
  style: { padding: 0 },
  hideIcon: true,
});

export const chooseWithModal = (
  dispatch: Dispatch, 
  titleID: string, 
  optionIDs: string[], 
  subtitleID?: string
): Promise<number> => {
  return new Promise<number>((resolve) => {
    const p: ChoiceModalExposedPropsI & { onClosing: () => void } = {
      titleID,
      optionIDs,
      subtitleID,
      onConfirm(pos) {
        resolve(pos);
      },
      onClosing() {
        resolve(-1);
      },
    };

    dispatch(show(MODALS_ENUM.CHOICE_MODAL, p));
  });
}